import moment from 'moment';
import { IS_ACTIVE } from 'common/constants/valuations';
import { PublicComp } from 'pages/ValuationsAllocation/types';
import { dbShortDate } from 'utilities';

/**
 * This function kicks in when the user is searching by company or ticker. Since we do not use
 * the measurement date in the comp groups page, we need a mechanism to determine if the company
 * is public or not. We do this by checking if the measurement date is between the first and last
 * pricing dates of the company or if it is still active but has a last pricing date in the past.
 * @param publicComp - The public comp data with pricing dates
 * @param marketDate - The market date to compare against (used as measurement date)
 * @returns true if the company is public, false otherwise
 */
export const isCompanyPublic = (
  publicComp: PublicComp | undefined,
  marketDate: string | undefined
): boolean => {
  if (!publicComp) {
    return false;
  }

  // We subtract 2 weeks because the last pricing date only happen on work days in the USA
  // so if the date is a holiday or a weekend, then the date of the data is the previous workday.
  // Also, in some other trading markets, like China, the market can be closed for as long as about a week.
  // So, we can assume the company is public if it has a last_pricing_date within the last 2 weeks.

  const firstPricingDateValue = publicComp.first_pricing_date?.value?.toString() ?? '';
  const lastPricingDateValue = publicComp.last_pricing_date?.value?.toString() ?? '';
  const securityActiveStatusValue = (publicComp.security_active_status?.value as string) ?? '';

  const firstPricingDate = dbShortDate(moment(firstPricingDateValue.replace('  ', ' '), 'MMM D YYYY hh:mmA', true));
  const lastPricingDate = dbShortDate(moment(lastPricingDateValue.replace('  ', ' '), 'MMM D YYYY hh:mmA', true));
  const isCompanyActive = securityActiveStatusValue === IS_ACTIVE || false;

  // Define date to use
  // marketDate is used on valuation page and current date is used on comp groups page
  const date = marketDate ?? dbShortDate();

  const twoWeeksAgo = dbShortDate(moment().subtract(2, 'w'));

  // check which date will be used: defined date or 2 weeks ago
  const lastDateForComparison = moment(date).isBefore(twoWeeksAgo) ? date : twoWeeksAgo;

  const isBetweenFirstAndLastPricingDates = moment(lastDateForComparison).isBetween(firstPricingDate, lastPricingDate);
  const isAfterOrSameThanFirstPricingDate = moment(lastDateForComparison).isSameOrAfter(firstPricingDate);
  const isLowerOrSameThanLastingDate = moment(lastDateForComparison).isSameOrBefore(lastPricingDate);

  // The company is public when...
  return (
    // The measurement date is between the dates of its first and last pricing date
    isBetweenFirstAndLastPricingDates ||
    // Or if it is after the last pricing date and is active or last date for comparison
    // is before than last pricing date.
    (isAfterOrSameThanFirstPricingDate && (isCompanyActive || isLowerOrSameThanLastingDate))
  );
};

/**
 * Gets the public comp from the API response for a specific comparison
 * @param comparison - The comparison object with cap_iq_id
 * @param publicCompsByDate - The public comps data from the API, keyed by date
 * @param marketDate - The market date to look up
 * @returns The PublicComp from the API response, or undefined if not found
 */
export const getPublicCompFromApi = (
  comparison: { cap_iq_id?: string },
  publicCompsByDate: Record<string, PublicComp[]> | undefined,
  marketDate: string | undefined
): PublicComp | undefined => {
  if (!publicCompsByDate || !marketDate) {
    return undefined;
  }

  // Format marketDate to match the key format in publicCompsByDate (ISO format: YYYY-MM-DD)
  const formattedMarketDate = dbShortDate(marketDate);
  if (!formattedMarketDate) {
    return undefined;
  }

  const publicCompsForDate = publicCompsByDate[formattedMarketDate];
  if (!publicCompsForDate) {
    return undefined;
  }

  const comparisonCapIqId = comparison.cap_iq_id;
  return publicCompsForDate.find((comp: PublicComp) => comp.cap_iq_id?.value === comparisonCapIqId);
};
